import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Check,
  ChevronDown,
  Currency,
  DollarSign,
  Euro,
  IndianRupee,
  JapaneseYen,
  PoundSterling,
  RussianRuble,
  SwissFranc,
  type LucideIcon,
} from 'lucide-react';
import * as Popover from '@radix-ui/react-popover';

import { api } from '../api/client';
import { cn } from './primitives';
import { ConfirmDialog, useConfirm } from './confirm-dialog';
import { useCurrencyStore } from '../lib/currency-store';
import { t } from '../../i18n';

// The shells and the tests import the store through this module, which is where
// it used to live. It moved to `lib` so hooks that print money can subscribe to
// it without importing a component.
export { useCurrencyStore } from '../lib/currency-store';

interface PublicBilling {
  baseCurrency: string;
  currencies: { code: string; rate: number; enabled?: boolean }[];
}

/**
 * The currency's own sign, drawn as an SVG rather than typed as a character.
 *
 * A glyph like ₹ depends on the font the viewer happens to have — on a machine
 * with no Indian font installed it renders as a hollow box, and a box where the
 * money sign should be makes the control look broken. These are paths, so they
 * draw identically everywhere and scale with the text beside them.
 *
 * Currencies with no sign of their own — AED, SGD, most of the world — share the
 * generic one deliberately: inventing a mark for them would be worse than a
 * neutral one beside the code that already names them.
 */
const CURRENCY_ICONS: Record<string, LucideIcon> = {
  INR: IndianRupee,
  USD: DollarSign,
  EUR: Euro,
  GBP: PoundSterling,
  JPY: JapaneseYen,
  RUB: RussianRuble,
  CHF: SwissFranc,
};

function CurrencyIcon({ code, className }: { code: string; className?: string }): React.JSX.Element {
  const Icon = CURRENCY_ICONS[code] ?? Currency;
  return <Icon className={cn('size-3.5 shrink-0 text-ink-2', className)} aria-hidden />;
}

/** "Indian Rupee", "US Dollar" — from the browser, so there is no list to keep. */
function currencyName(code: string): string {
  try {
    const name = new Intl.DisplayNames(undefined, { type: 'currency' }).of(code);
    return name && name !== code ? name : code;
  } catch {
    return code;
  }
}

/**
 * Reads amounts in a currency of the viewer's choosing.
 *
 * It converts for reading only. The money itself stays in the currency it was
 * billed in, figures are converted at the platform's daily rate, and anything payable asks
 * `formatMoney` for `exact` and is never touched — a resident who pays a
 * converted number pays the wrong amount.
 *
 * The choice is per browser rather than per account: it is a preference about
 * reading, not a property of the society, and two people looking at the same
 * society may want different ones.
 */
export function useViewCurrency(): {
  code: string | null;
  options: { code: string; rate: number }[];
  choose: (code: string | null) => void;
} {
  const { data } = useQuery({
    queryKey: ['public', 'billing'],
    queryFn: () => api.get<PublicBilling>('/public/billing', { anonymous: true }),
    staleTime: 5 * 60_000,
    retry: false,
  });

  const code = useCurrencyStore((state) => state.code);
  const choose = useCurrencyStore((state) => state.set);
  const setRates = useCurrencyStore((state) => state.setRates);

  const options = React.useMemo(
    () => (data?.currencies ?? []).filter((c) => c.enabled !== false),
    [data?.currencies],
  );

  // A currency with no rate cannot be converted into honestly, so it is simply
  // not offered. Handing the rates to the store re-applies the stored choice,
  // so a reload does not quietly drop back to unconverted amounts.
  React.useEffect(() => {
    if (!data) return;
    setRates(
      data.baseCurrency,
      Object.fromEntries(options.map((option) => [option.code, option.rate])),
    );
  }, [data, options, setRates]);

  return { code, options, choose };
}

/**
 * The app-bar control — the only place currency is chosen.
 *
 * In a society console it also persists the currency that society is billed in,
 * so picking AED here is not merely a way of reading the page: the next invoice
 * is in AED too. That is why the subscription page no longer carries its own
 * "Pay in" dropdown — two controls for one decision is how they end up
 * disagreeing.
 *
 * It is a popover rather than a `<select>` for one reason: a native option list
 * can hold text and nothing else, so the sign of each currency could not be
 * drawn in it at all.
 */
export function CurrencySwitch({
  className,
  persist = false,
}: {
  className?: string;
  /** Society console: also set the society's billing currency. */
  persist?: boolean;
}): React.JSX.Element | null {
  const queryClient = useQueryClient();
  const { code, options, choose } = useViewCurrency();
  const own = useCurrencyStore((state) => state.own);
  const setOwn = useCurrencyStore((state) => state.setOwn);
  const [open, setOpen] = React.useState(false);
  // Changing what a society is billed in is worth one deliberate confirmation.
  // The app's own dialog rather than `window.confirm`, which draws the
  // browser's chrome — a grey box with the hostname in it, no relation to the
  // page it interrupts, and nothing it can be styled with.
  const confirming = useConfirm<string>();

  // A society has one currency: residents' dues and the society's own
  // subscription both move together. Two settings for one idea is how a console
  // ends up showing AED on the bills page and USD on the subscription page.
  const bill = useMutation({
    mutationFn: async (next: string) => {
      // Dues first: it is the one every screen and the resident app read.
      await api.put('/api/v1/societies/me/payments', { currency: next });
      await api.put('/api/v1/subscriptions/checkout/currency', { currency: next });
    },
    // Moved immediately so the bar answers the click, then confirmed by the
    // bootstrap refetch below. `own` is what changes here — not a reading
    // preference — because this *is* the society's currency now.
    onMutate: (next: string) => {
      const previous = own;
      setOwn(next);
      return { previous };
    },
    onSuccess: async () => {
      toast.success(t('Currency changed'), {
        description: t('Bills, receipts and the resident app now use it.'),
      });
      // Every cached amount in the console is now in the old currency: the
      // change re-denominates the society's money server-side, so bills,
      // payments, the dashboard, accounting and reports are all stale at once.
      // Naming three query keys left the rest showing the old figures until
      // the page was reloaded by hand.
      await queryClient.invalidateQueries();
    },
    // Rolled back, so the bar never claims a currency the society is not
    // actually billed in — the exact mismatch this control is meant to end.
    onError: (error, _next, context) => {
      if (context?.previous) setOwn(context.previous);
      toast.error(error instanceof Error ? error.message : t('Could not change the currency.'));
    },
  });

  if (options.length < 2) return null;

  // A society console has no "reading" currency to show: the society is billed
  // in exactly one, and this control sets it. Showing a stored view preference
  // here is what put AED in the app bar above a page reading "Billed in INR".
  const showing = persist ? own : code ?? own;

  // Every entry reads the same way: the currency's name and its code. In a
  // society console picking one changes what residents are billed; in the
  // platform console it changes what the prices are shown in. Neither is worth
  // hedging in the list — the amounts themselves say what they are.
  const list: string[] = [own, ...options.filter((option) => option.code !== own).map((o) => o.code)];

  /**
   * The list hands over a plain currency code; what that means is the console's
   * business, not the row's.
   *
   * It used to hand over `null` for the society's own currency, which is how
   * the society console broke: every row there is a real currency, so every row
   * sent `null`, and `null` means "the one we are already on" — so clicking any
   * of them did nothing at all.
   */
  const pick = (next: string) => {
    setOpen(false);
    if (persist) {
      if (next === own) return;
      confirming.ask(next);
      return;
    }
    // Picking the currency the money is really in is how you stop converting.
    const choice = next === own ? null : next;
    if (choice === code) return;
    choose(choice);
  };

  const pending = confirming.target;

  return (
    <>
    {/* Same height, border and type as the language picker beside it, so the
        app bar reads as one row of controls rather than a row of odd ones. */}
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label={
            persist
              ? t('Bill this society in {0}', [currencyName(showing)])
              : t('Show amounts in {0}', [currencyName(showing)])
          }
          aria-haspopup="listbox"
          aria-expanded={open}
          title={
            persist
              ? t('Every bill, receipt and amount in the resident app is in {0}.', [showing])
              : t('Converted for reading only. Anything payable stays in {0}.', [own])
          }
          className={cn(
            'inline-flex h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-sk-md border border-outline-field bg-surface px-2.5 text-[13px] font-semibold text-ink-1 transition-colors hover:border-indigo/60 focus-visible:border-indigo focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo/25',
            className,
          )}
        >
          <CurrencyIcon code={showing} />
          <span>{showing}</span>
          <ChevronDown className="size-3.5 text-ink-3" aria-hidden />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={4}
          collisionPadding={12}
          className="z-[60] min-w-[12rem] rounded-sk-lg border border-outline bg-surface p-1 shadow-sk-lg focus-visible:outline-none"
        >
          <ul role="listbox" aria-label={persist ? t('Bill this society in') : t('Show amounts in')}>
            {list.map((entry) => {
              const active = entry === showing;
              return (
                <li key={entry} role="option" aria-selected={active}>
                  <button
                    type="button"
                    onClick={() => pick(entry)}
                    className={cn(
                      'flex w-full items-center gap-2.5 rounded-sk-md px-2.5 py-2 text-left text-[13px] hover:bg-surface-variant',
                      active ? 'font-semibold text-indigo-ink' : 'text-ink-1',
                    )}
                  >
                    <CurrencyIcon code={entry} className={active ? 'text-indigo' : undefined} />
                    <span className="min-w-0 flex-1">
                      <span className="block">{currencyName(entry)}</span>
                      <span className="block text-2xs text-ink-3">{entry}</span>
                    </span>
                    {active ? <Check className="size-4 shrink-0 text-indigo" aria-hidden /> : null}
                  </button>
                </li>
              );
            })}
          </ul>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>

    {persist ? (
      <ConfirmDialog
        open={confirming.open}
        onOpenChange={confirming.onOpenChange}
        tone="default"
        title={t('Bill this society in {0}?', [pending ?? ''])}
        description={
          <>
            <p>
              {t('Every bill, receipt and amount in the resident app changes to {0}. Only the payment methods that settle {0} will be offered.', [
                pending ?? '',
              ])}
            </p>
            <p className="mt-2">
              {t('Amounts already invoiced keep the currency they were raised in.')}
            </p>
          </>
        }
        confirmLabel={t('Change to {0}', [pending ?? ''])}
        loading={bill.isPending}
        onConfirm={() => {
          if (pending) bill.mutate(pending);
          confirming.dismiss();
        }}
      />
    ) : null}
    </>
  );
}
